import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { PlayerMediaSelect } from "@/pages/player/form/PlayerMediaSelect";
import { CampaignType } from "@/lib/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const formSchema = z.object({
  name: z.string().min(1),
  isCombineAndShuffle: z.boolean(),
  contentId: z.number().optional().or(z.null()),
  type: z.string(),
});

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
};

export const CampaignMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading, defaultValues }) => {
  const { t } = useTranslation();
  const [showWarningModal, setShowWarningModal] = useState(false);

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      name: "",
      isCombineAndShuffle: true,
      type: CampaignType.Standard.toString(),
    },
  });

  const campaignType = form.watch("type");
  const isStandardType = campaignType === CampaignType.Standard.toString();
  const isConditionalType = campaignType === CampaignType.Conditional.toString();

  const handleSwitchChange = (checked: boolean) => {
    if (!checked) {
      // User is trying to disable, show warning modal
      setShowWarningModal(true);
    } else {
      // User is enabling, allow immediately
      form.setValue("isCombineAndShuffle", true);
    }
  };

  const handleConfirmDisable = () => {
    form.setValue("isCombineAndShuffle", false);
    setShowWarningModal(false);
  };

  const handleCancelDisable = () => {
    setShowWarningModal(false);
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.name")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("common.name")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.type")}</FormLabel>
                <FormControl>
                  <Select defaultValue={field.value} onValueChange={(value) => field.onChange(value)}>
                    <SelectTrigger className="w-[120px] h-[32px]">
                      <SelectValue placeholder={t("common.type")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CampaignType.Standard.toString()}>{t("Standard")}</SelectItem>
                      <SelectItem value={CampaignType.Robot.toString()}>{t("Robot")}</SelectItem>
                      {/* <SelectItem value={CampaignType.Conditional.toString()}>{t("Conditional")}</SelectItem> */}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {(isStandardType || isConditionalType) && (
            <>
              <FormField
                control={form.control}
                name="isCombineAndShuffle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("campaign.isCombineAndShuffle")}</FormLabel>
                    <FormControl>
                      <div>
                        <Switch checked={field.value} onCheckedChange={handleSwitchChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* @ts-ignore */}
              <PlayerMediaSelect
                form={form}
                visibleTypes={["content"]}
                label={t("content.defaultContent")}
                properties={{
                  contentId: "contentId",
                }}
              />

              <FormField
                control={form.control}
                name="contentId"
                render={() => (
                  <FormItem>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <Button type="submit" disabled={isLoading}>
            {buttonText}
            {isLoading && <LoadingSmall />}
          </Button>
        </form>
      </Form>

      <AlertDialog open={showWarningModal} onOpenChange={setShowWarningModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure want to disable?</AlertDialogTitle>
            <AlertDialogDescription>
              If you disable, in the player only campaign content will be visible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDisable}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisable}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
